'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, Calendar, Fuel, Bot, AlertCircle, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { useT } from '@/lib/useT';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

function timeAgo(date: string, t: (k: string) => string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notif_ago_now');
  if (mins < 60) return `${mins} ${t('notif_ago_min')}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${t('notif_ago_hour')}`;
  const days = Math.floor(hours / 24);
  return `${days} ${t('notif_ago_day')}`;
}

function stripHtml(str: string): string {
  return str.replace(/<[^>]*>/g, '');
}

function typeIcon(type: string) {
  switch (type) {
    case 'LEAVE_REQUEST': return <Calendar size={14} />;
    case 'FUEL_LOG': return <Fuel size={14} />;
    case 'AI_INSIGHT': return <Bot size={14} />;
    default: return <AlertCircle size={14} />;
  }
}

function typeColor(type: string): string {
  switch (type) {
    case 'LEAVE_REQUEST': return '#FFD60A';
    case 'FUEL_LOG': return '#14A76C';
    case 'AI_INSIGHT': return '#A855F7';
    case 'ALERT': return '#EF4444';
    default: return 'var(--c-accent)';
  }
}

// Short two-tone chime for urgent (ALERT) notifications — Web Audio, no asset.
function chime() {
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = 880; g.gain.value = 0.07;
    o.start();
    o.frequency.setValueAtTime(660, ctx.currentTime + 0.13);
    setTimeout(() => { try { o.stop(); ctx.close(); } catch { /* noop */ } }, 280);
  } catch { /* audio unavailable */ }
}

export default function NotificationBell() {
  const t = useT();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<Notification | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const prevCount = useRef(0);

  const fetchCount = useCallback(() => {
    api.get('/notifications/count').then((r: any) => {
      const c = r.count;
      // When the unread count rises (and not on first load), chime for urgent
      // (ALERT) notifications — e.g. a red-priority Work-Plan task.
      if (c > prevCount.current && prevCount.current !== 0) {
        api.get('/notifications').then((data: Notification[]) => {
          if (Array.isArray(data) && data.some(n => !n.isRead && n.type === 'ALERT')) chime();
        }).catch(() => {});
      }
      prevCount.current = c;
      setCount(c);
    }).catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    api.get('/notifications').then((data: Notification[]) => {
      setNotifications(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Poll count every 30 seconds
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch full list when opened
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (notif: Notification) => {
    if (!notif.isRead) {
      api.patch(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
      setCount(prev => Math.max(0, prev - 1));
    }
    if (notif.link) {
      setOpen(false);
      router.push(notif.link);
    } else {
      // Open detail modal for notifications without link (SOS, alerts, etc.)
      setDetail(notif);
    }
  };

  const markAllRead = async () => {
    api.patch('/notifications/read-all').catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        title={t('notif_title')}
        className="nav-item w-10 h-9 group relative"
        style={{ color: open ? 'var(--c-accent)' : 'var(--c-muted)' }}
      >
        <Bell size={17} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
            style={{ background: '#FF3B3B', lineHeight: 1 }}>
            {count > 99 ? '99+' : count}
          </span>
        )}
        <span className="absolute left-full ml-3 px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap
          opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-50"
          style={{ background: 'var(--c-panel)', color: 'var(--c-text)', border: '1px solid var(--c-border)', boxShadow: '0 4px 12px var(--c-shadow)' }}>
          {t('notif_title')}
        </span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute left-full ml-3 bottom-0 w-80 max-h-[480px] flex flex-col rounded-xl overflow-hidden z-[100]"
          style={{
            background: 'var(--c-panel)',
            border: '1px solid var(--c-border)',
            boxShadow: '0 8px 32px var(--c-shadow)',
          }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0"
            style={{ borderBottom: '1px solid var(--c-hover)' }}>
            <h3 className="text-sm font-bold" style={{ color: 'var(--c-heading)' }}>
              {t('notif_title')}
            </h3>
            {count > 0 && (
              <button onClick={markAllRead}
                className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-lg transition-all"
                style={{ color: 'var(--c-accent)', background: 'color-mix(in srgb, var(--c-accent) 8%, transparent)' }}>
                <Check size={11} />
                {t('notif_mark_all')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"
                  style={{ color: 'var(--c-muted)' }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Bell size={28} style={{ color: 'var(--c-border2)' }} />
                <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('notif_empty')}</p>
              </div>
            ) : (
              notifications.map(n => (
                <button key={n.id} onClick={() => markRead(n)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left transition-all"
                  style={{
                    background: n.isRead ? 'transparent' : 'color-mix(in srgb, var(--c-accent) 4%, transparent)',
                    borderBottom: '1px solid var(--c-hover)',
                  }}>
                  {/* Icon */}
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: `${typeColor(n.type)}15`, color: typeColor(n.type) }}>
                    {typeIcon(n.type)}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--c-heading)' }}>
                        {n.type === 'LEAVE_REQUEST' ? t('notif_leave')
                          : n.type === 'FUEL_LOG' ? t('notif_fuel')
                          : n.type === 'AI_INSIGHT' ? t('notif_ai')
                          : n.title}
                      </p>
                      {!n.isRead && (
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--c-accent)' }} />
                      )}
                    </div>
                    <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--c-text)' }}>
                      {stripHtml(n.message)}
                    </p>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--c-muted)' }}>
                      {timeAgo(n.createdAt, t)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          onClick={() => setDetail(null)}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.6)' }} />
          <div className="relative w-full max-w-md rounded-2xl overflow-hidden"
            style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3"
              style={{
                borderBottom: '1px solid var(--c-border)',
                background: detail.type === 'ALERT' ? 'rgba(239,68,68,0.08)' : 'transparent',
              }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: detail.type === 'ALERT' ? 'rgba(239,68,68,0.15)' : `${typeColor(detail.type)}15`,
                  color: detail.type === 'ALERT' ? '#EF4444' : typeColor(detail.type),
                }}>
                {detail.type === 'ALERT' ? <AlertCircle size={20} /> : typeIcon(detail.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm" style={{ color: detail.type === 'ALERT' ? '#EF4444' : 'var(--c-heading)' }}>
                  {detail.title}
                </p>
                <p className="text-[11px]" style={{ color: 'var(--c-muted)' }}>
                  {new Date(detail.createdAt).toLocaleString('de')}
                </p>
              </div>
            </div>
            {/* Body */}
            <div className="px-5 py-4">
              <p className="text-sm whitespace-pre-line leading-relaxed" style={{ color: 'var(--c-text)' }}>
                {stripHtml(detail.message)}
              </p>
            </div>
            {/* Footer */}
            <div className="px-5 py-3 flex justify-end" style={{ borderTop: '1px solid var(--c-border)' }}>
              <button onClick={() => setDetail(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: 'var(--c-hover)', color: 'var(--c-heading)' }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
